import NotFound from '../NotFound';
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { BookOpen, Clock, ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';
import { SEOLayout, AlertBox } from '../../components/SEOLayout';
import { allGuidesData as guidesData } from '../../data/seoDataMerged';
import { Button } from '../../components/ui/button';
import { IataSourceLink } from '../../data/iataRules';
import CrateCalculator from '../../components/CrateCalculator';

const md = (text) => text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));

const GuideDetail = () => {
  const { slug } = useParams();
  const guide = guidesData[slug];

  if (!guide) return <NotFound />;

  return (
    <SEOLayout
      title={guide.seoTitle}
      description={guide.seoDescription}
      keywords={guide.seoKeywords}
      breadcrumbs={[
        { label: 'Guides', href: '/guides' },
        { label: guide.title }
      ]}
    >
      {/* Article Header */}
      <article className="max-w-4xl mx-auto">
        <header className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/guides" className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              All Guides
            </Link>
            <span className="text-slate-300">|</span>
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm capitalize">
              {guide.category.replace(/-/g, ' ')}
            </span>
            <span className="flex items-center gap-1 text-slate-500 text-sm">
              <Clock className="w-4 h-4" />
              {guide.readTime} read
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {guide.title}
          </h1>
          
          <p className="text-xl text-slate-600 leading-relaxed">
            {guide.introduction}
          </p>
          
          <IataSourceLink className="mt-4" />
        </header>

        {/* CTA Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 mb-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-bold text-lg mb-1">Check your crate before the airport</h3>
              <p className="text-blue-100 text-sm">IATA size formula + photo inspection. Independent service, not affiliated with IATA or any airline.</p>
            </div>
            <Link to="/">
              <Button className="bg-white text-blue-600 hover:bg-blue-50 whitespace-nowrap">
                Check My Crate - $39
              </Button>
            </Link>
          </div>
        </div>

        {slug === 'iata-crate-size-calculator' && <CrateCalculator />}

        {/* Article Content */}
        <div className="prose prose-slate prose-lg max-w-none">
          {guide.sections?.map((section, index) => (
            <section key={index} className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-start gap-3">
                {section.title}
              </h2>
              
              {/* Render content with line breaks preserved */}
              {section.content.split('\n\n').map((paragraph, pIndex) => {
                // Check if it's a list
                if (paragraph.includes('•') || paragraph.includes('✅') || paragraph.includes('❌') || paragraph.includes('⚠️')) {
                  return (
                    <div key={pIndex} className="my-4 bg-slate-50 rounded-lg p-4">
                      {paragraph.split('\n').map((line, lIndex) => {
                        if (line.trim().startsWith('✅')) {
                          return (
                            <div key={lIndex} className="flex items-start gap-2 text-green-700 mb-2">
                              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                              <span>{md(line.replace('✅', '').trim())}</span>
                            </div>
                          );
                        } else if (line.trim().startsWith('❌')) {
                          return (
                            <div key={lIndex} className="flex items-start gap-2 text-red-700 mb-2">
                              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                              <span>{md(line.replace('❌', '').trim())}</span>
                            </div>
                          );
                        } else if (line.trim().startsWith('⚠️')) {
                          return (
                            <div key={lIndex} className="flex items-start gap-2 text-amber-700 mb-2">
                              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                              <span>{md(line.replace('⚠️', '').trim())}</span>
                            </div>
                          );
                        } else if (line.trim().startsWith('•')) {
                          return (
                            <div key={lIndex} className="flex items-start gap-2 text-slate-700 mb-2 ml-2">
                              <span className="text-blue-600">•</span>
                              <span>{md(line.replace('•', '').trim())}</span>
                            </div>
                          );
                        } else if (line.trim()) {
                          return <p key={lIndex} className="text-slate-700 font-medium mb-2">{md(line)}</p>;
                        }
                        return null;
                      })}
                    </div>
                  );
                }
                
                // Check if it's a code/formula block
                if (paragraph.includes('Length =') || paragraph.includes('Width =') || paragraph.includes('Height =')) {
                  return (
                    <div key={pIndex} className="my-4 bg-blue-50 border border-blue-200 rounded-lg p-4 font-mono text-sm">
                      {paragraph.split('\n').map((line, lIndex) => (
                        <div key={lIndex} className="text-blue-800">{md(line)}</div>
                      ))}
                    </div>
                  );
                }

                // Check for warning text
                if (paragraph.includes('WHY IT MATTERS') || paragraph.includes('SOLUTION') || paragraph.includes('IMPORTANT')) {
                  return (
                    <div key={pIndex} className="my-4 bg-amber-50 border-l-4 border-amber-400 p-4">
                      <p className="text-amber-900">{md(paragraph)}</p>
                    </div>
                  );
                }

                // Regular paragraph
                return <p key={pIndex} className="text-slate-700 leading-relaxed mb-4">{md(paragraph)}</p>;
              })}
            </section>
          ))}

          {/* Conclusion */}
          {guide.conclusion && (
            <section className="mt-12 bg-slate-100 rounded-xl p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-3">Conclusion</h2>
              <p className="text-slate-700">{guide.conclusion}</p>
            </section>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-red-900 mb-3">
            Check Your Crate Before You Fly
          </h3>
          <p className="text-red-700 mb-6 max-w-2xl mx-auto">
            Enter your pet's measurements and upload 3 crate photos. We apply the IATA size formula and check
            what is visible: bolts, door, ventilation on four sides, bowls. Independent service, not affiliated with IATA or any airline.
          </p>
          <Link to="/">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8">
              Check Your Crate Now - $39
            </Button>
          </Link>
        </div>

        {/* Related Guides */}
        <div className="mt-12">
          <h3 className="text-xl font-bold text-slate-900 mb-4">Related Guides</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {Object.values(guidesData)
              .filter(g => g.slug !== slug)
              .slice(0, 3)
              .map(relatedGuide => (
                <Link
                  key={relatedGuide.slug}
                  to={`/guides/${relatedGuide.slug}`}
                  className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <h4 className="font-semibold text-slate-900 mb-1">{relatedGuide.title}</h4>
                  <p className="text-slate-600 text-sm line-clamp-2">{relatedGuide.introduction}</p>
                </Link>
              ))}
          </div>
        </div>
      </article>
    </SEOLayout>
  );
};

export default GuideDetail;
